// Templates de Notificações Push - Tom Humano e Contextual

export interface NotificationTemplate {
  id: string
  title: string
  body: string
  urgency: 'critical' | 'high' | 'medium'
  type: 'new_request' | 'nearby_match' | 'urgent_need' | 'thank_you' | 'achievement'
}

// Notificações de Novos Pedidos
export const NEW_REQUEST_NOTIFICATIONS = {
  critical: {
    id: 'new_critical',
    title: '🚨 Pedido Urgente Perto de Você',
    body: 'Alguém com {bloodType} precisa de ajuda em {city}. A distância é de apenas {distance}km. Você pode salvar uma vida agora!',
    urgency: 'critical' as const,
    type: 'new_request' as const
  },
  high: {
    id: 'new_high',
    title: '⚠️ Nova Solicitação: {bloodType}',
    body: 'Paciente em {city} precisa de doação {bloodType}. Você está a {distance}km de distância. Pode ajudar?',
    urgency: 'high' as const,
    type: 'new_request' as const
  },
  medium: {
    id: 'new_medium',
    title: '🩸 Alguém Precisa de {bloodType}',
    body: 'Nova solicitação em {city} ({distance}km). Sua doação pode fazer a diferença!',
    urgency: 'medium' as const,
    type: 'new_request' as const
  }
}

// Notificações de Match Próximo
export const NEARBY_MATCH_NOTIFICATIONS = {
  perfect_match: {
    id: 'perfect_match',
    title: '🎯 Match Perfeito!',
    body: 'Encontramos alguém com seu tipo sanguíneo a apenas {distance}km. Hospital: {hospital}',
    urgency: 'high' as const,
    type: 'nearby_match' as const
  },
  close_match: {
    id: 'close_match',
    title: '📍 Pedido Próximo a Você',
    body: '{patientName} precisa de {bloodType} em {city}. Você pode ajudar hoje?',
    urgency: 'medium' as const,
    type: 'nearby_match' as const
  }
}

// Notificações de Urgência
export const URGENT_NOTIFICATIONS = {
  time_sensitive: {
    id: 'time_sensitive',
    title: '⏰ Tempo Crítico!',
    body: 'Pedido {bloodType} em {hospital} precisa de resposta nas próximas horas. Você está disponível?',
    urgency: 'critical' as const,
    type: 'urgent_need' as const
  },
  multiple_units: {
    id: 'multiple_units',
    title: '🩸 Múltiplas Bolsas Necessárias',
    body: '{unitsNeeded} bolsas de {bloodType} urgentes em {city}. Compartilhe com amigos doadores!',
    urgency: 'high' as const,
    type: 'urgent_need' as const
  }
}

// Notificações de Agradecimento
export const THANK_YOU_NOTIFICATIONS = {
  post_donation: {
    id: 'post_donation',
    title: '💝 Você é Incrível!',
    body: 'Sua doação pode ter salvado até 4 vidas. Obrigado por ser um herói de verdade! +100 pontos',
    urgency: 'medium' as const,
    type: 'thank_you' as const
  },
  impact_reminder: {
    id: 'impact_reminder',
    title: '🌟 Seu Impacto',
    body: 'Você já salvou {livesSaved} vidas com suas {donations} doações. Continue inspirando!',
    urgency: 'medium' as const,
    type: 'thank_you' as const
  }
}

// Notificações de Conquistas
export const ACHIEVEMENT_NOTIFICATIONS = {
  new_badge: {
    id: 'new_badge',
    title: '🏆 Nova Medalha Desbloqueada!',
    body: 'Parabéns! Você conquistou: {badgeName}. +{points} pontos!',
    urgency: 'medium' as const,
    type: 'achievement' as const
  },
  level_up: {
    id: 'level_up',
    title: '⬆️ Subiu de Nível!',
    body: 'Você agora é {levelName}! Novos benefícios desbloqueados.',
    urgency: 'medium' as const,
    type: 'achievement' as const
  },
  milestone: {
    id: 'milestone',
    title: '🎉 Marco Histórico!',
    body: '{donations} doações! Você faz parte dos {percentage}% melhores doadores.',
    urgency: 'medium' as const,
    type: 'achievement' as const
  }
}

// Função para formatar notificação com dados dinâmicos
export function formatNotification(
  template: NotificationTemplate,
  data: Record<string, string | number>
): { title: string; body: string } {
  let title = template.title
  let body = template.body

  Object.entries(data).forEach(([key, value]) => {
    const placeholder = `{${key}}`
    title = title.replace(placeholder, String(value))
    body = body.replace(placeholder, String(value))
  })

  return { title, body }
}

// Exemplos de uso:
// const notification = formatNotification(NEW_REQUEST_NOTIFICATIONS.critical, {
//   bloodType: 'O+',
//   city: 'São Paulo',
//   distance: '2.5'
// })
// Result: "🚨 Pedido Urgente Perto de Você"
//         "Alguém com O+ precisa de ajuda em São Paulo. A distância é de apenas 2.5km..."
